import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  packsForRoles,
  machineStepComplete,
  roleEntryPath,
  defaultActivePack,
  packBlockedReason,
  packPrerequisitesMet,
  flowsForEntry,
  guidedFlowById,
  resolveFlowOrgStatus,
  resolveFlowUiState,
  primaryCtaLabel,
  ALL_CAPABILITY_PACKS,
  type CapabilityPack,
  type GuidedFlowState,
  type MachineJourneyId,
  type MachineReadinessSignals,
  type PeopleOrgSignals,
  type FoundationsSignals,
} from '@wapharma/domain';
import { unwrapData } from '@wapharma/shared';
import { apiGet } from '../api';
import { getToken } from '../auth';
import { useAuth } from '../contexts/AuthContext';
import { filterJourneyPacksForUser, journeyPackById, journeyPackTitle, type JourneyPack } from '../journeys';
import { flowMeta, flowMetaTitle } from '../journeys/flowMeta';
import { useJourneyProgress } from './useJourneyProgress';
import {
  getSkippedFlowIds,
  isCompanionDismissed,
  isGuideAutoOpenDismissed,
  setCompanionDismissed,
  setGuideAutoOpenDismissed,
  setLastGuidePackId,
  setSkippedFlowIds,
  skipFlowId,
} from '../lib/guidePrefs';
import { subscribeOpenGuide } from '../lib/guideShell';
import {
  getSetupWizardUiState,
  subscribeSetupWizardUiState,
} from '../lib/setupShell';
import { useTour } from '../contexts/TourContext';
import { hasTourForFlow } from '../tours/definitions';
import { TOURS_ENABLED } from '../tours/flags';

export type MachineReadinessPayload = {
  signals: MachineReadinessSignals;
  visibleIds: MachineJourneyId[];
  nextStepId: MachineJourneyId | null;
  people?: PeopleOrgSignals;
  flows?: GuidedFlowState[];
  nextFlowId?: string | null;
  foundations?: FoundationsSignals;
  foundationsComplete?: boolean;
  foundationsProgressPercent?: number;
};

export type ChecklistRow = {
  flow: GuidedFlowState;
  title: string;
  /** Guide finished (learning COMPLETED) — strikethrough in dock. */
  done: boolean;
  /** Org + learning both complete — required to Continuer to next pack. */
  packReady: boolean;
  href: string | null;
  skippable: boolean;
  /** Clickable: not done and prerequisites met (incl. skipped / learning-done). */
  interactive: boolean;
};

export type ChecklistGroup = {
  entryId: MachineJourneyId;
  title: string;
  rows: ChecklistRow[];
  doneCount: number;
};

function resolveDefaultPack(
  packId: MachineJourneyId | null | undefined,
  roles: readonly string[],
  visibleIds: readonly MachineJourneyId[],
  signals: MachineReadinessSignals | undefined,
  fallback: MachineJourneyId | null,
): MachineJourneyId | null {
  if (packId) return packId;
  if (signals && visibleIds.length > 0) {
    const fromRole = defaultActivePack(roles, visibleIds, signals);
    if (fromRole) return fromRole;
  }
  return fallback;
}

export function useGuideShell() {
  const { user, mustChangePassword } = useAuth();
  const navigate = useNavigate();
  const tour = useTour();
  const { learningStatus, refresh: refreshProgress } = useJourneyProgress();
  const [activePackId, setActivePackId] = useState<MachineJourneyId | null>(null);
  const [activeFlowId, setActiveFlowId] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<MachineReadinessPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [dockOpen, setDockOpen] = useState(false);
  const [parcoursStripNudge, setParcoursStripNudge] = useState(false);
  const [companionDismissed, setCompanionDismissedState] = useState(() =>
    isCompanionDismissed(user?.id),
  );
  const [skippedFlowIds, setSkippedFlowIdsState] = useState<string[]>(() =>
    getSkippedFlowIds(user?.id),
  );
  /** Setup owns first-run until dismissed — companion must not stack under it. */
  const [setupWizardVisible, setSetupWizardVisible] = useState(
    () => getSetupWizardUiState().visible,
  );
  const prevNextStepRef = useRef<MachineJourneyId | null>(null);

  useEffect(() => {
    setCompanionDismissedState(isCompanionDismissed(user?.id));
    setSkippedFlowIdsState(getSkippedFlowIds(user?.id));
  }, [user?.id]);

  useEffect(() => {
    return subscribeSetupWizardUiState(() => {
      setSetupWizardVisible(getSetupWizardUiState().visible);
    });
  }, []);

  const roles = user?.roles ?? [];
  const isSuperAdmin = roles.includes('SUPER_ADMIN');
  const userPacks = useMemo((): CapabilityPack[] => {
    if (user?.packs?.length) {
      return user.packs.filter((p): p is CapabilityPack =>
        (ALL_CAPABILITY_PACKS as readonly string[]).includes(p),
      );
    }
    return [...packsForRoles(roles)];
  }, [user?.packs, roles]);

  const visiblePacks: JourneyPack[] = useMemo(
    () => filterJourneyPacksForUser(userPacks, { isSuperAdmin }),
    [userPacks, isSuperAdmin],
  );

  const visibleIds = useMemo(() => visiblePacks.map((p) => p.id), [visiblePacks]);

  const entryPath = useMemo(() => roleEntryPath(roles, visibleIds), [roles, visibleIds]);
  const entryPathSet = useMemo(() => new Set(entryPath), [entryPath]);

  const refreshReadiness = useCallback(async () => {
    const token = getToken();
    if (!token) return null;
    setLoading(true);
    try {
      const raw = await apiGet<{ data: MachineReadinessPayload } | MachineReadinessPayload>(
        '/reports/machine-readiness',
        token,
      );
      void refreshProgress().catch(() => undefined);
      const payload = unwrapData(raw) as MachineReadinessPayload;
      setReadiness(payload);
      return payload;
    } finally {
      setLoading(false);
    }
  }, [refreshProgress]);

  const clearParcoursStripNudge = useCallback(() => {
    setParcoursStripNudge(false);
  }, []);

  const offerParcoursCollapsed = useCallback(
    (opts?: { packId?: MachineJourneyId | null }) => {
      if (!TOURS_ENABLED) return;
      void (async () => {
        let payload = readiness;
        if (payload?.foundationsComplete !== true) {
          payload = (await refreshReadiness()) ?? payload;
        }
        if (payload?.foundationsComplete !== true) return;

        if (companionDismissed) {
          setCompanionDismissed(user?.id, false);
          setCompanionDismissedState(false);
        }
        const target = resolveDefaultPack(
          opts?.packId ?? activePackId,
          roles,
          payload.visibleIds ?? visibleIds,
          payload.signals,
          payload.nextStepId ?? visiblePacks[0]?.id ?? null,
        );
        if (target) {
          setActivePackId(target);
          if (user?.id) setLastGuidePackId(user.id, target);
        }
        setDockOpen(false);
        setParcoursStripNudge(true);
      })();
    },
    [
      activePackId,
      companionDismissed,
      readiness,
      refreshReadiness,
      roles,
      user?.id,
      visibleIds,
      visiblePacks,
    ],
  );

  const openDock = useCallback(
    (opts?: { packId?: MachineJourneyId | null; refresh?: boolean }) => {
      if (!TOURS_ENABLED) return;
      void (async () => {
        let payload = readiness;
        if (opts?.refresh || payload?.foundationsComplete !== true) {
          payload = (await refreshReadiness()) ?? payload;
        }
        /* Parcours waits on Configuration initiale foundations. */
        if (payload?.foundationsComplete !== true) return;

        if (companionDismissed) {
          setCompanionDismissed(user?.id, false);
          setCompanionDismissedState(false);
        }
        const target = resolveDefaultPack(
          opts?.packId ?? activePackId,
          roles,
          payload.visibleIds ?? visibleIds,
          payload.signals,
          payload.nextStepId ?? visiblePacks[0]?.id ?? null,
        );
        if (target) {
          setActivePackId(target);
          if (user?.id) setLastGuidePackId(user.id, target);
        }
        setParcoursStripNudge(false);
        setDockOpen(true);
      })();
    },
    [
      activePackId,
      companionDismissed,
      readiness,
      refreshReadiness,
      roles,
      user?.id,
      visibleIds,
      visiblePacks,
    ],
  );

  const closeDock = useCallback(() => {
    setDockOpen(false);
    setParcoursStripNudge(false);
    setGuideAutoOpenDismissed(user?.id, true);
  }, [user?.id]);

  /** @deprecated Prefer openDock — kept for subscribeOpenGuide / empty states. */
  const openGuide = useCallback(
    (packId?: MachineJourneyId | null) => {
      openDock({ packId });
    },
    [openDock],
  );

  const closeGuide = useCallback(() => {
    setDockOpen(false);
    setParcoursStripNudge(false);
  }, []);

  const dismissAutoOpen = useCallback(() => {
    setGuideAutoOpenDismissed(user?.id, true);
  }, [user?.id]);

  const dismissCompanion = useCallback(() => {
    setCompanionDismissed(user?.id, true);
    setCompanionDismissedState(true);
    setGuideAutoOpenDismissed(user?.id, true);
    setDockOpen(false);
    setParcoursStripNudge(false);
  }, [user?.id]);

  const reopenCompanion = useCallback(() => {
    setCompanionDismissed(user?.id, false);
    setCompanionDismissedState(false);
    openDock();
  }, [openDock, user?.id]);

  const skipChecklistFlow = useCallback(
    (flowId: string) => {
      skipFlowId(user?.id, flowId);
      setSkippedFlowIdsState(getSkippedFlowIds(user?.id));
    },
    [user?.id],
  );

  const clearSkippedFlows = useCallback(() => {
    setSkippedFlowIds(user?.id, []);
    setSkippedFlowIdsState([]);
  }, [user?.id]);

  const selectPack = useCallback(
    (packId: MachineJourneyId) => {
      setActivePackId(packId);
      setActiveFlowId(null);
      if (user?.id) setLastGuidePackId(user.id, packId);
    },
    [user?.id],
  );

  const flowUi = useCallback(
    (flowId: string, orgStatus: GuidedFlowState['status']) => {
      const rev = guidedFlowById(flowId)?.revision ?? 1;
      return resolveFlowUiState(orgStatus, learningStatus(flowId, rev));
    },
    [learningStatus],
  );

  const activateChecklistFlow = useCallback(
    (flowId: string) => {
      const def = guidedFlowById(flowId);
      if (def) {
        setActivePackId(def.entryId);
        setActiveFlowId(flowId);
        if (user?.id) setLastGuidePackId(user.id, def.entryId);
      }
      const meta = flowMeta(flowId);
      setDockOpen(false);
      tour.stopTour();
      if (meta?.href) {
        navigate(meta.href);
        if (TOURS_ENABLED) {
          window.setTimeout(() => {
            if (hasTourForFlow(flowId)) tour.startFlowTour(flowId);
          }, 280);
        }
      } else if (TOURS_ENABLED) {
        window.setTimeout(() => {
          if (hasTourForFlow(flowId)) tour.startFlowTour(flowId);
        }, 80);
      }
    },
    [navigate, tour, user?.id],
  );

  useEffect(() => {
    return subscribeOpenGuide((req) => {
      openDock({
        packId: (req.packId as MachineJourneyId | undefined) ?? null,
        refresh: req.refresh === true,
      });
    });
  }, [openDock]);

  useEffect(() => {
    if (!TOURS_ENABLED) return;
    if (!user || mustChangePassword) return;
    if (isCompanionDismissed(user.id)) return;
    if (isGuideAutoOpenDismissed(user.id)) return;
    /* Soft steps (stock bas / péremption) still run after hard foundations — wait. */
    if (getSetupWizardUiState().visible) return;
    let cancelled = false;
    void (async () => {
      const payload = await refreshReadiness();
      if (cancelled || !payload) return;
      if (isGuideAutoOpenDismissed(user.id)) return;
      if (getSetupWizardUiState().visible) return;
      /* Foundations incomplete: SetupWizard owns first-run; dock waits (no strip either). */
      if (payload.foundationsComplete === false) {
        return;
      }
      const flows = payload.flows?.filter((f) => f.status !== 'unavailable') ?? [];
      if (flows.length === 0) return;
      const target = resolveDefaultPack(
        null,
        user.roles,
        payload.visibleIds,
        payload.signals,
        payload.nextStepId,
      );
      if (target) setActivePackId(target);
      /* First session: expand + focus until user closes (pref set in closeDock). */
      setDockOpen(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, mustChangePassword, refreshReadiness]);

  useEffect(() => {
    if (readiness?.foundationsComplete === false && dockOpen) {
      setDockOpen(false);
    }
  }, [readiness?.foundationsComplete, dockOpen]);

  const activePack = visiblePacks.find((p) => p.id === activePackId) ?? visiblePacks[0] ?? null;

  const packFlows = useMemo((): GuidedFlowState[] => {
    if (!activePack) return [];
    const entryFlows = flowsForEntry(activePack.id).filter((f) => f.availability !== 'unavailable');
    const entryFlowIds = new Set(entryFlows.map((f) => f.id));

    if (readiness?.flows?.length) {
      return readiness.flows.filter(
        (f) => entryFlowIds.has(f.id) && f.status !== 'unavailable',
      );
    }

    if (!readiness?.signals) return [];

    const ctx = {
      signals: readiness.signals,
      people: readiness.people,
    };
    const completed = new Set<string>();
    const states: GuidedFlowState[] = [];
    for (const flow of entryFlows) {
      const state = resolveFlowOrgStatus(flow.id, ctx, completed);
      if (state.status === 'unavailable') continue;
      states.push(state);
      if (state.status === 'complete') completed.add(flow.id);
    }
    return states;
  }, [activePack, readiness?.flows, readiness?.signals, readiness?.people]);

  const checklistRows = useMemo((): ChecklistRow[] => {
    const skipped = new Set(skippedFlowIds);
    const source =
      readiness?.flows?.filter((f) => f.status !== 'unavailable') ??
      ([] as GuidedFlowState[]);

    /** Prereqs satisfied by org complete, learning done, or user skip. */
    const satisfied = new Set<string>(skipped);
    for (const flow of source) {
      if (skipped.has(flow.id)) continue;
      const ui = flowUi(flow.id, flow.status);
      if (flow.status === 'complete' || ui === 'done' || ui === 'continue') {
        satisfied.add(flow.id);
      }
    }

    const rows: ChecklistRow[] = [];
    for (const flow of source) {
      if (skipped.has(flow.id)) continue;
      const ui = flowUi(flow.id, flow.status);
      /* continue = learning done, org still open — mark tour done in dock. */
      const done = ui === 'done' || ui === 'continue';
      const packReady = ui === 'done';
      const meta = flowMeta(flow.id);
      const def = guidedFlowById(flow.id);
      const prereqsMet = (def?.prerequisites ?? []).every((p) => satisfied.has(p));
      const interactive = !done && prereqsMet;
      rows.push({
        flow,
        title: meta?.title ?? flowMetaTitle(flow.id),
        done,
        packReady,
        href: meta?.href ?? null,
        skippable: interactive,
        interactive,
      });
    }
    return rows;
  }, [flowUi, readiness?.flows, skippedFlowIds]);

  useEffect(() => {
    const next = readiness?.nextStepId ?? null;
    if (!next) return;
    /*
     * Do not yank the dock off Personnes while guided flows there are still open.
     * Machine `people` completes at staffAccountCount >= 1; Parcours needs the tours.
     */
    if (activePackId === 'people') {
      const peopleRows = checklistRows.filter(
        (r) => guidedFlowById(r.flow.id)?.entryId === 'people',
      );
      const peopleStillOpen =
        peopleRows.length === 0 || peopleRows.some((r) => !r.packReady);
      if (peopleStillOpen) {
        prevNextStepRef.current = next;
        return;
      }
    }
    if (prevNextStepRef.current !== next) {
      setActivePackId(next);
      prevNextStepRef.current = next;
    }
  }, [readiness?.nextStepId, activePackId, checklistRows]);

  const checklistGroups = useMemo((): ChecklistGroup[] => {
    const buckets = new Map<MachineJourneyId, ChecklistRow[]>();
    for (const row of checklistRows) {
      const entryId = guidedFlowById(row.flow.id)?.entryId;
      if (!entryId) continue;
      const list = buckets.get(entryId) ?? [];
      list.push(row);
      buckets.set(entryId, list);
    }
    const orderIds =
      visiblePacks.length > 0
        ? visiblePacks.map((p) => p.id)
        : ([...buckets.keys()] as MachineJourneyId[]);
    const groups: ChecklistGroup[] = [];
    for (const entryId of orderIds) {
      const rows = buckets.get(entryId);
      if (!rows?.length) continue;
      const pack = journeyPackById(entryId);
      groups.push({
        entryId,
        title: pack?.shortTitle ?? journeyPackTitle(entryId),
        rows,
        doneCount: rows.filter((r) => r.done).length,
      });
      buckets.delete(entryId);
    }
    for (const [entryId, rows] of buckets) {
      if (!rows.length) continue;
      const pack = journeyPackById(entryId);
      groups.push({
        entryId,
        title: pack?.shortTitle ?? journeyPackTitle(entryId),
        rows,
        doneCount: rows.filter((r) => r.done).length,
      });
    }
    return groups;
  }, [checklistRows, visiblePacks]);

  const progressPercent = useMemo(() => {
    if (checklistRows.length === 0) return 0;
    const done = checklistRows.filter((r) => r.done).length;
    return Math.round((done / checklistRows.length) * 100);
  }, [checklistRows]);

  const doneCount = useMemo(
    () => checklistRows.filter((r) => r.done).length,
    [checklistRows],
  );

  const remainingCount = useMemo(
    () => checklistRows.filter((r) => !r.done).length,
    [checklistRows],
  );

  const nextChecklistRow = useMemo(
    () => checklistRows.find((r) => r.interactive) ?? null,
    [checklistRows],
  );

  const resumeProgress = useCallback(() => {
    if (nextChecklistRow) {
      activateChecklistFlow(nextChecklistRow.flow.id);
      return;
    }
    openDock();
  }, [activateChecklistFlow, nextChecklistRow, openDock]);

  const nextFlowInPack = useMemo(() => {
    for (const f of packFlows) {
      const ui = flowUi(f.id, f.status);
      if (ui !== 'done') return f;
    }
    return null;
  }, [packFlows, flowUi]);

  useEffect(() => {
    if (activeFlowId) return;
    if (nextFlowInPack) setActiveFlowId(nextFlowInPack.id);
  }, [activeFlowId, nextFlowInPack]);

  const isPackComplete = useCallback(
    (packId: MachineJourneyId) => {
      if (!readiness?.signals) return false;
      return machineStepComplete(packId, readiness.signals);
    },
    [readiness?.signals],
  );

  const isPackBlocked = useCallback(
    (packId: MachineJourneyId) => {
      if (!readiness?.signals) return false;
      return !packPrerequisitesMet(packId, readiness.signals);
    },
    [readiness?.signals],
  );

  const blockedReason = useCallback(
    (packId: MachineJourneyId): string | null => {
      if (!readiness?.signals) return null;
      const upstream = packBlockedReason(packId, readiness.signals);
      if (!upstream) return null;
      return journeyPackTitle(upstream);
    },
    [readiness?.signals],
  );

  const blockedFlowReason = useCallback((flow: GuidedFlowState): string | null => {
    if (!flow.blockedBy) return null;
    return flowMetaTitle(flow.blockedBy);
  }, []);

  const showCompanion =
    TOURS_ENABLED &&
    readiness?.foundationsComplete === true &&
    !companionDismissed &&
    checklistRows.length > 0 &&
    !setupWizardVisible;

  return {
    openGuide,
    openDock,
    offerParcoursCollapsed,
    clearParcoursStripNudge,
    parcoursStripNudge,
    closeDock,
    closeGuide,
    dockOpen,
    showCompanion,
    companionDismissed,
    dismissCompanion,
    reopenCompanion,
    dismissAutoOpen,
    selectPack,
    selectFlow: setActiveFlowId,
    activateChecklistFlow,
    skipChecklistFlow,
    clearSkippedFlows,
    checklistRows,
    checklistGroups,
    progressPercent,
    doneCount,
    remainingCount,
    nextChecklistRow,
    resumeProgress,
    activePack,
    activePackId: activePack?.id ?? null,
    activeFlowId,
    packFlows,
    nextFlowInPack,
    flowUi,
    primaryCtaLabel,
    visiblePacks,
    readiness,
    loading,
    refreshReadiness,
    isPackComplete,
    isPackBlocked,
    blockedReason,
    blockedFlowReason,
    entryPath,
    entryPathSet,
  };
}
